// src/components/CreateEventPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, serverTimestamp, getDocs, query, where
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

const ADMIN_EMAIL = 'urano@hgu.jp';
const DOW = ['日', '月', '火', '水', '木', '金', '土'];
const TIME_PRESETS = ['終日', '午前', '午後', '10:00〜', '13:00〜', '15:00〜', '18:00〜', '19:00〜'];

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const dow = DOW[d.getDay()];
  return `${d.getFullYear()}/${mm}/${dd}(${dow})`;
}

export default function CreateEventPage() {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [allowed,       setAllowed]       = useState(null);
  const [requestStatus, setRequestStatus] = useState(null); // null | 'pending' | 'none'
  const [requesting,    setRequesting]    = useState(false);
  const [requestDone,   setRequestDone]   = useState(false);

  const [title,    setTitle]    = useState('');
  const [memo,     setMemo]     = useState('');
  const [slots,    setSlots]    = useState([]);
  const [dateVal,  setDateVal]  = useState('');
  const [timeVal,  setTimeVal]  = useState('終日');
  const [deadline, setDeadline] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  useEffect(() => {
    if (!user) return;
    if (user.email === ADMIN_EMAIL) { setAllowed(true); return; }

    // 許可チェック
    const q = query(collection(db, 'allowedCreators'), where('email', '==', user.email.toLowerCase()));
    getDocs(q).then(snap => {
      if (!snap.empty) { setAllowed(true); return; }
      setAllowed(false);
      // 申請済みかチェック
      const rq = query(collection(db, 'creatorRequests'), where('email', '==', user.email.toLowerCase()), where('status', '==', 'pending'));
      getDocs(rq).then(rsnap => {
        setRequestStatus(rsnap.empty ? 'none' : 'pending');
      });
    });
  }, [user]);

  async function submitRequest() {
    setRequesting(true);
    try {
      await addDoc(collection(db, 'creatorRequests'), {
        uid:       user.uid,
        email:     user.email.toLowerCase(),
        name:      user.displayName || user.email,
        photoURL:  user.photoURL || null,
        status:    'pending',
        createdAt: serverTimestamp(),
      });
      setRequestStatus('pending');
      setRequestDone(true);
    } catch (e) {
      alert('申請に失敗しました。もう一度お試しください。');
    }
    setRequesting(false);
  }

  if (!user) {
    return (
      <div className="page"><div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p className="mb-2">イベントを作成するにはGoogleでログインしてください</p>
          <button className="btn btn-primary" onClick={loginWithGoogle}>Googleでログイン</button>
        </div>
      </div></div>
    );
  }

  if (allowed === null) {
    return <div className="page"><div className="container"><div className="spinner" /></div></div>;
  }

  if (allowed === false) {
    return (
      <div className="page"><div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <p className="font-medium" style={{ fontSize: 16, marginBottom: 8 }}>作成権限がありません</p>
          <p className="text-sm text-muted" style={{ marginBottom: 24 }}>
            イベントの作成は管理者が許可したアカウントのみ可能です。<br />
            参加者としてURLから回答することは引き続き可能です。
          </p>

          {requestStatus === 'none' && !requestDone && (
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.08)', paddingTop: 20 }}>
              <p className="text-sm" style={{ marginBottom: 12 }}>
                作成権限が必要な場合は、管理者に申請できます。
              </p>
              <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '10px 14px', marginBottom: 16, textAlign: 'left' }}>
                <div className="text-sm font-medium">{user.displayName}</div>
                <div className="text-sm text-muted">{user.email}</div>
              </div>
              <button className="btn btn-primary" onClick={submitRequest} disabled={requesting}>
                {requesting ? '申請中…' : '作成権限を申請する'}
              </button>
            </div>
          )}

          {(requestStatus === 'pending' || requestDone) && (
            <div style={{ borderTop: '0.5px solid rgba(0,0,0,0.08)', paddingTop: 20 }}>
              <div className="msg-info" style={{ textAlign: 'left' }}>
                <div className="font-medium" style={{ marginBottom: 4 }}>✓ 申請済み</div>
                <div className="text-sm">
                  管理者が確認後、作成権限が付与されます。<br />
                  承認されたら再度ログインしてください。
                </div>
              </div>
            </div>
          )}
        </div>
      </div></div>
    );
  }

  // 以下、作成権限ありの場合
  function addSlot() {
    if (!dateVal) return;
    const label = formatDateLabel(dateVal);
    if (slots.find(s => s.date === dateVal && s.time === timeVal)) return;
    setSlots(prev => [...prev, { id: Date.now(), date: dateVal, label, time: timeVal }]);
    setTimeVal('終日');
  }

  function removeSlot(id) {
    setSlots(prev => prev.filter(s => s.id !== id));
  }

  async function handleCreate() {
    if (!title.trim()) { setError('イベント名を入力してください'); return; }
    if (slots.length === 0) { setError('候補日を1つ以上追加してください'); return; }
    setError('');
    setLoading(true);
    try {
      const sortedSlots = [...slots].sort((a, b) => a.date.localeCompare(b.date));
      const docRef = await addDoc(collection(db, 'events'), {
        title:     title.trim(),
        memo:      memo.trim(),
        hostUid:   user.uid,
        hostName:  user.displayName || user.email,
        hostEmail: user.email,
        hostPhoto: user.photoURL || null,
        slots:     sortedSlots,
        deadline:  deadline || null,
        createdAt: serverTimestamp(),
        closedAt:  null,
      });
      navigate(`/event/${docRef.id}`);
    } catch (e) {
      setError('作成に失敗しました。ネットワーク接続を確認してください。');
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <div className="container">
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: '1rem' }}>新しいイベントを作成</h1>

        <div className="card">
          <div className="card-title">基本情報</div>
          <div className="mb-2">
            <label className="field-label">イベント名 *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="例：〇月定例会議 日程調整" />
          </div>
          <div className="mb-2">
            <label className="field-label">メモ・説明（任意）</label>
            <textarea value={memo} onChange={e => setMemo(e.target.value)}
              placeholder="会議の目的、場所、Zoom URLなど" />
          </div>
          <div>
            <label className="field-label">回答締め切り（任意）</label>
            <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
              style={{ maxWidth: 200 }} />
          </div>
        </div>

        <div className="card">
          <div className="card-title">候補日時の設定</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)}
              style={{ flex: '1', minWidth: 140 }} />
            <input type="text" value={timeVal} onChange={e => setTimeVal(e.target.value)}
              placeholder="時間帯" style={{ flex: '1', minWidth: 120 }} />
            <button className="btn btn-primary btn-sm" onClick={addSlot} disabled={!dateVal}>
              追加
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {TIME_PRESETS.map(t => (
              <button key={t} className="btn btn-sm"
                style={{
                  padding: '4px 10px', fontSize: 12,
                  background: timeVal === t ? '#E1F5EE' : '',
                  borderColor: timeVal === t ? '#1D9E75' : '',
                  color: timeVal === t ? '#0F6E56' : '',
                }}
                onClick={() => setTimeVal(t)}>{t}</button>
            ))}
          </div>
          {slots.length === 0 ? (
            <p className="text-sm text-muted">候補日を追加してください</p>
          ) : (
            <>
              <p className="text-sm text-muted mb-1">
                同じ日に複数追加する場合は、日付をそのままにして時間帯を変えて「追加」を押してください
              </p>
              <div className="slot-grid">
                {[...slots].sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)).map(s => (
                  <div key={s.id} className="slot-chip">
                    <div>
                      <div style={{ fontWeight: 500 }}>{s.label}</div>
                      <div style={{ fontSize: 12, marginTop: 2 }}>{s.time}</div>
                    </div>
                    <button className="slot-chip-remove" onClick={() => removeSlot(s.id)}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {error && <div className="msg-error mb-2">{error}</div>}

        <button className="btn btn-primary btn-full" onClick={handleCreate} disabled={loading}>
          {loading
            ? <><span className="spinner" style={{ width: 16, height: 16 }} /> 作成中…</>
            : 'イベントを作成してURLを発行'}
        </button>
        <p className="text-sm text-muted text-center mt-1">作成後、共有URLが発行されます</p>
      </div>
    </div>
  );
}
